import { useState, type ReactNode } from "react";
import { Button } from "../../ui";

/** Cursor pagination state shared by the admin list pages (docs/07 §3). */
export function useCursorPages() {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  return {
    cursor: cursors[cursors.length - 1],
    reset: () => setCursors([undefined]),
    canGoBack: cursors.length > 1,
    back: () => setCursors(cursors.slice(0, -1)),
    next: (cursor: string | null | undefined) =>
      setCursors([...cursors, cursor ?? undefined]),
  };
}

export function Pager({
  pages,
  nextCursor,
}: {
  pages: ReturnType<typeof useCursorPages>;
  nextCursor: string | null | undefined;
}) {
  return (
    <div className="mt-4 flex gap-2">
      <Button
        variant="secondary"
        disabled={!pages.canGoBack}
        onClick={pages.back}
      >
        Previous
      </Button>
      <Button
        variant="secondary"
        disabled={!nextCursor}
        onClick={() => pages.next(nextCursor)}
      >
        Next
      </Button>
    </div>
  );
}

export function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
      {label}
      <select
        className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Action button that support sees disabled with the "Read-only role" tooltip. */
export function StaffAction({
  allowed,
  onClick,
  variant = "secondary",
  children,
  title = "Read-only role",
}: {
  allowed: boolean;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger";
  children: ReactNode;
  title?: string;
}) {
  return (
    <span title={allowed ? undefined : title}>
      <Button variant={variant} disabled={!allowed} onClick={onClick}>
        {children}
      </Button>
    </span>
  );
}

export function stateTone(
  state: string,
): "neutral" | "success" | "warning" | "danger" {
  if (["succeeded", "delivered", "active", "fulfilled"].includes(state))
    return "success";
  if (["pending", "held", "refunded"].includes(state)) return "warning";
  if (["failed", "dead", "disputed", "disabled", "expired"].includes(state))
    return "danger";
  return "neutral";
}
